/**
 * Fix K — ONE vocabulary for "is there actually a game on this board".
 *
 * Fix G (ARCHITECTURE §9) made `railState === 'tournament'` render the
 * ordinary Stage + Rail — the idle wait for a pairing, with NO game of mine
 * and no watch up — exactly like a live game, so `rail/Rail/model.ts`'s
 * `LIVE_LAYOUT_STATES` could keep meaning "render the board layout" without
 * growing a special case. Every unit that read `LIVE_LAYOUT_STATES` (or
 * `body === 'live'`) ALONE to decide whether IT should exist therefore
 * started showing on the idle view too: the Draw / Resign / Abort action
 * bar, the pinned move nav, the engine — none of which make sense with no
 * game (owner, 2026-10-06: "the resign / draw options are visible without
 * any game running — run an agent to detect and fix such inconsistencies").
 *
 * `LIVE_LAYOUT_STATES` keeps its own meaning unchanged: the idle view DOES
 * draw a (disabled, dimmed) board, the tab strip and the icon row. What
 * changes is everything further down the tree — a unit that assumes "a game
 * of mine (or a watch) is actually on the board" reads ITS predicate from
 * here instead, so the rule lives in ONE file and the next such unit cannot
 * repeat the bug (the owner's ask was to "fix the class, not the instance").
 *
 * `surfaceFor` reads the core store exactly once (`selectRailState` /
 * `selectBoardSource` / `game.phase` / `watch`); every predicate below is a
 * pure function of ITS result, so a model that reads one of them pays
 * exactly one boolean subscription, same as every other leaf in this tree
 * (ARCHITECTURE §5). Several call sites already have a cheaper, equivalent
 * input on hand (`rail/Rail/model.ts` already takes `railState` as a plain
 * argument) — those derive the SAME boolean locally rather than paying for
 * a second subscription, and say so in a comment next to it; this file is
 * still the rule they have to stay equal to, which `__tests__/
 * surfaceMatrix.test.tsx` pins down across all 8 states.
 */
import type {LiveGameState} from '@/Store/liveGameStore';
import {selectBoardSource, selectRailState} from '@/Store/liveGame/selectors';

import {isPageState} from '../chrome/pageStates';

/**
 * `game.phase`, with `evalPending` folded into `ended` — `selectGameOver`'s
 * own rule: the opening report is still running, but the game is over for
 * every "can I still do X" question this file answers (the action bar's
 * `over` face already treats the two the same way).
 */
export type MyGame = 'none' | 'starting' | 'playing' | 'ended';

export type Surface = {
  /** the Stage (board + two PlayerRows) is mounted at all */
  board: boolean;
  myGame: MyGame;
  /** a live watch OR a replay owns the board */
  watching: boolean;
  /** tournament open, nothing of mine (or a watch) up — the G5 idle wait */
  tournamentIdle: boolean;
  /** my own game, or a watched one, has a result to show */
  result: boolean;
};

const myGameFor = (phase: LiveGameState['game']['phase']): MyGame =>
  phase === 'evalPending' ? 'ended' : phase;

export function surfaceFor(s: LiveGameState): Surface {
  const railState = selectRailState(s);
  const watching = selectBoardSource(s) === 'watch';
  return {
    board: !isPageState(railState),
    myGame: myGameFor(s.game.phase),
    watching,
    tournamentIdle: railState === 'tournament',
    result: railState === 'result' || (watching && !!s.watch.ended),
  };
}

/**
 * A seat exists: a game of mine (including the pre-game countdown) or a
 * watch — `stage/PlayerRow`'s own `hasSeat`, `rail/tabs/tabSpec.ts`'s
 * `gameOnBoard`.
 */
const seated = (surface: Surface): boolean =>
  surface.myGame !== 'none' || surface.watching;

/**
 * The notation / game controls make sense: playing, ended, or a watch —
 * never the idle wait (`myGame === 'none'`) and never the pre-game countdown
 * (`myGame === 'starting'`, which keeps `MatchedCard` only — it has no game
 * actions of its own).
 */
const actionable = (surface: Surface): boolean =>
  surface.watching ||
  surface.myGame === 'playing' ||
  surface.myGame === 'ended';

/**
 * rail/live/ActionBar + rail/live/Toolbar: Draw/Resign/Abort/Berserk,
 * Stop watching, or Rematch/New — never with no game (the idle tournament
 * wait) and never during the pre-game countdown.
 */
export const showActionBar = (s: LiveGameState): boolean =>
  actionable(surfaceFor(s));

/** rail/live/MoveNav — pinned under every tab, so it needs the same guard. */
export const showMoveNav = (s: LiveGameState): boolean =>
  actionable(surfaceFor(s));

/**
 * rail/live/EventDock — nested in the Moves tab, which `rail/tabs/
 * tabSpec.ts` already drops while idle (`gameOnBoard`); read here too so the
 * dock does not depend on that tab mechanic alone to stay hidden.
 */
export const showEventDock = (s: LiveGameState): boolean =>
  actionable(surfaceFor(s));

/**
 * stage/Clock + stage/Captured — `stage/PlayerRow`'s own `hasSeat`,
 * re-exported here as the one place the rule is written down (neither
 * model reads this directly today: the PARENT already does not mount
 * either leaf without a seat, which is unit convention §1's "the bodies
 * that are not the current state do not exist in the tree" applied one
 * level down).
 */
export const showClocks = (s: LiveGameState): boolean => seated(surfaceFor(s));
export const showCaptured = (s: LiveGameState): boolean =>
  seated(surfaceFor(s));

/**
 * rail/tabs/tabSpec.ts's `gameOnBoard` — the SAME formula as `showClocks`,
 * named for its own caller so a future reader does not have to prove the
 * two are the same thing by hand.
 */
export const showMovesTab = (s: LiveGameState): boolean =>
  seated(surfaceFor(s));

/**
 * stage/PlayerRow/FirstMoveBanner — only while MY OWN game is actually
 * playing (never a watch: there is no first move of mine to owe in a game
 * that is not mine, `FirstMoveBanner.model.ts`'s own rule).
 */
export const showFirstMoveBanner = (s: LiveGameState): boolean => {
  const surface = surfaceFor(s);
  return !surface.watching && surface.myGame === 'playing';
};

/** stage/BoardCountdown — the 3 · 2 · 1, only for MY OWN game's pre-game beat. */
export const showBoardCountdown = (s: LiveGameState): boolean => {
  const surface = surfaceFor(s);
  return !surface.watching && surface.myGame === 'starting';
};

/**
 * rail/live/RoundChip — playing or ended, never the idle wait and never a
 * watch (a spectator reads the round from the tournament tabs they came
 * from, M4 §2 — `RoundChip.model.ts`'s own `!watching`, repeated here).
 */
export const showRoundChip = (s: LiveGameState): boolean => {
  const surface = surfaceFor(s);
  return (
    !surface.watching &&
    (surface.myGame === 'playing' || surface.myGame === 'ended')
  );
};

/**
 * stage/EvalBar + rail/analysis/EngineHost: a real board behind the engine.
 * `analysisStore.on` ALONE is not enough — it can outlive the game it was
 * turned on for (a live watch allows it the instant the watched game ends,
 * `rail/actions/tiles/AnalysisAction.model.ts`'s own docblock, and leaving
 * that watch does not reset it), so without this an engine left on behind a
 * finished watch stayed on over the next idle tournament wait's board too. A
 * caller ANDs this with its own store's `on` / `mode !== 'off'`.
 */
export const showEvalBar = (s: LiveGameState): boolean =>
  !surfaceFor(s).tournamentIdle;

/** stage/TournamentIdle — the G5 card. */
export const showTournamentIdle = (s: LiveGameState): boolean =>
  surfaceFor(s).tournamentIdle;

/**
 * overlay/ResultOverlay — whether there is a result SIGNAL at all. The
 * overlay's own model additionally ANDs this with `useV2Ui.resultDismissed`
 * (mine) or `watchResultReady` (a spectator's 1.5 s delay), both UI-only
 * state this core-only file cannot see — `ResultOverlay/model.ts` already
 * derives `visible` correctly from `selectResultView` / `selectWatchEnded`;
 * this predicate exists so the matrix test has one name to assert against,
 * documented here so the two cannot silently drift apart.
 */
export const showResultOverlay = (s: LiveGameState): boolean =>
  surfaceFor(s).result;

/**
 * rail/actions/RailActions — which FACE the icon row wears. The individual
 * tiles keep their own fair-play gate (`rail/actions/gates.ts`'s
 * `selectActionGate` / `settled`); this is the coarse label the surface
 * matrix test reads to describe the row as a whole.
 */
export type RailActionsFace = 'lobby' | 'inGame' | 'watch' | 'tournamentIdle';

export const railActionsFace = (s: LiveGameState): RailActionsFace => {
  const surface = surfaceFor(s);
  if (surface.tournamentIdle) {
    return 'tournamentIdle';
  }
  if (surface.watching) {
    return 'watch';
  }
  return surface.myGame === 'playing' ? 'inGame' : 'lobby';
};

export default surfaceFor;
